import { useEffect, useMemo, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import { Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './HomePage.module.css';
import heroImage from '../assets/images/lab-logo.png';
import mitLogo from '../assets/images/mit-logo.png';
import typography from '../theme/typography';
import colors from '../theme/colors';
import { isContentfulConfigured } from '../contentful/client';
import { fetchSponsorLogosForScroll } from '../contentful/sponsorLogosForScrollApi';
import {
  fetchNewestLabHomePageContent,
  isLabHomePageDataComplete,
} from '../contentful/labHomePageContentApi';
import { resolveAssetUrl } from '../contentful/utils';

import ibmLogo from '../assets/images/sponsors/IBM_logo.png';
import accentureLogo from '../assets/images/sponsors/accenture-logo.png';
import ccesLogo from '../assets/images/sponsors/cces-logo.png';
import darpaLogo from '../assets/images/sponsors/darpa-logo.png';
import jameelLogo from '../assets/images/sponsors/jameel-clinic-logo.png';
import nsfLogo from '../assets/images/sponsors/nsf-logo.png';
import sesLogo from '../assets/images/sponsors/ses-logo.png';
import xylemLogo from '../assets/images/sponsors/xylem-logo.png';

import highlightImg1 from '../assets/images/in-the-news/mit.png';
import highlightImg2 from '../assets/images/in-the-news/meet-the-team.png';
import highlightImg3 from '../assets/images/in-the-news/cents.png';

const FALLBACK_SPONSORS = [
  { id: 'fallback-ibm', src: ibmLogo, alt: 'IBM' },
  { id: 'fallback-jameel', src: jameelLogo, alt: 'Jameel Clinic' },
  { id: 'fallback-accenture', src: accentureLogo, alt: 'Accenture' },
  { id: 'fallback-ses', src: sesLogo, alt: 'SES' },
  { id: 'fallback-cces', src: ccesLogo, alt: 'CCES' },
  { id: 'fallback-darpa', src: darpaLogo, alt: 'DARPA' },
  { id: 'fallback-xylem', src: xylemLogo, alt: 'Xylem' },
  { id: 'fallback-nsf', src: nsfLogo, alt: 'NSF' },
  { id: 'fallback-mit', src: mitLogo, alt: 'MIT' },
];

const HERO_LOREM_FALLBACK = (
  <>Aenean vulputate eleifend tellus. Aenean leo ligula, porttitor eu, consequat vitae, eleifend ac, enim. Aliquam lorem
  ante dapibus in viverra quis, feugiat a, tellus. Wam quam nunc, blandit vel, luctus pulvinar, hendrerit id.</>
);

const FALLBACK_HIGHLIGHTS = [
  {
    key: 'fallback-h1',
    image: highlightImg1,
    badge: 'MIT News',
    title: 'MIT researchers use large language models to flag problems in complex systems',
    link: 'https://news.mit.edu/2024/researchers-use-large-language-models-to-flag-problems-0814',
    external: true,
  },
  {
    key: 'fallback-h2',
    image: highlightImg2,
    title: 'Meet the Team',
    link: '/lab/people',
    external: false,
  },
  {
    key: 'fallback-h3',
    image: highlightImg3,
    title: 'CENTS: Generating synthetic electricity consumption time series for rare and unseen scenarios',
    link: 'https://arxiv.org/abs/2501.14426',
    external: true,
  },
];

function isExternalUrl(url) {
  const u = String(url).trim();
  return u.startsWith('http://') || u.startsWith('https://') || u.startsWith('//');
}

function toRouterPath(url) {
  const u = String(url).trim();
  if (u.startsWith('/')) {
    return u;
  }
  return `/${u}`;
}

/**
 * @param {Map<string, any>} assetsById
 */
function buildHomeHeroRichTextOptions(assetsById) {
  return {
    renderMark: {
      [MARKS.BOLD]: (text) => (
        <strong style={{ fontWeight: 700, color: colors.dark }}>{text}</strong>
      ),
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
      [MARKS.CODE]: (text) => <code>{text}</code>,
      [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
      [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
      [MARKS.STRIKETHROUGH]: (text) => <s>{text}</s>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
      [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
      [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
      [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
      [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
      [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
      [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
      [BLOCKS.HR]: () => <hr className={styles.heroRule} />,
      [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const url = resolveAssetUrl(node.data?.target, assetsById);
        if (!url) {
          return null;
        }
        return <img src={url} alt="" className={styles.heroEmbeddedImage} loading="lazy" />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: () => null,
      [BLOCKS.EMBEDDED_RESOURCE]: () => null,
      [BLOCKS.TABLE]: (node, children) => (
        <div className={styles.heroTableWrap}>
          <table className={styles.heroTable}>
            <tbody>{children}</tbody>
          </table>
        </div>
      ),
      [BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
      [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th scope="col">{children}</th>,

      [INLINES.HYPERLINK]: (node, children) => {
        const href = node.data?.uri || '#';
        if (isExternalUrl(href)) {
          return (
            <a href={href} className={styles.heroStatementLink} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        }
        return (
          <Link to={toRouterPath(href)} className={styles.heroStatementLink}>
            {children}
          </Link>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const url = resolveAssetUrl(node.data?.target, assetsById);
        if (!url) {
          return <span className={styles.heroStatementLink}>{children}</span>;
        }
        return (
          <a href={url} className={styles.heroStatementLink} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <span className={styles.richTextEntryLink}>{children}</span>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node, children) => (
        <span className={styles.richTextEntryInline}>{children}</span>
      ),
      [INLINES.EMBEDDED_RESOURCE]: () => null,
      [INLINES.RESOURCE_HYPERLINK]: (node, children) => {
        const uri = node.data?.uri;
        if (uri && (uri.startsWith('http') || uri.startsWith('//'))) {
          return (
            <a href={uri} className={styles.heroStatementLink} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        }
        return <span className={styles.richTextEntryLink}>{children}</span>;
      },
    },
  };
}

const HomePage = () => {
  usePageMeta(pagesSeo.home);
  const [sponsors, setSponsors] = useState(FALLBACK_SPONSORS);
  const [homeContent, setHomeContent] = useState(null);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const rows = await fetchSponsorLogosForScroll();
        if (cancelled) {
          return;
        }
        if (rows.length > 0) {
          setSponsors(rows);
        }
      } catch (err) {
        console.error('Failed to load sponsor logos from Contentful', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchNewestLabHomePageContent();
        if (cancelled) {
          return;
        }
        if (isLabHomePageDataComplete(data)) {
          setHomeContent(data);
        }
      } catch (err) {
        console.error('Failed to load lab home page content from Contentful', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const heroRichTextOptions = useMemo(
    () => (homeContent ? buildHomeHeroRichTextOptions(homeContent.assetsById) : null),
    [homeContent]
  );

  const useCms = homeContent && isLabHomePageDataComplete(homeContent) && heroRichTextOptions;
  const highlights = useCms
    ? homeContent.highlights.map((h) => ({
        key: h.key,
        image: h.imageUrl,
        title: h.title,
        link: h.link,
        external: isExternalUrl(h.link),
        badge: null,
      }))
    : FALLBACK_HIGHLIGHTS;

  return (
    <>
      <div className={styles.page}>
        <Header />

        <section className={styles.hero}>
          <div className={styles.heroLeft}>
            <img src={heroImage} alt="Data to AI Lab" className={styles.heroImage} />
          </div>
          <div className={styles.heroRight}>
            <h1 className={styles.heroHeading}>DATA TO AI LAB</h1>
            {useCms ? (
              <div className={styles.heroStatement}>
                {documentToReactComponents(
                  homeContent.labStatementDocument,
                  heroRichTextOptions
                )}
              </div>
            ) : (
              <p className={styles.heroDescription}>{HERO_LOREM_FALLBACK}</p>
            )}
          </div>
        </section>

        <section className={styles.sponsors}>
          <p className={styles.sponsorsTitle}>WITH SUPPORT FROM</p>
          <div className={styles.sponsorsTrack}>
            <div className={styles.sponsorsSlide}>
              {sponsors.map((sponsor) => (
                <div key={sponsor.id} className={styles.sponsorLogo}>
                  <img src={sponsor.src} alt={sponsor.alt} />
                </div>
              ))}
            </div>
            <div className={styles.sponsorsSlide}>
              {sponsors.map((sponsor) => (
                <div key={`${sponsor.id}-dup`} className={styles.sponsorLogo}>
                  <img src={sponsor.src} alt={sponsor.alt} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.highlights}>
          <div className={styles.highlightsGrid}>
            {highlights.map((item) => {
              const cardContent = (
                <>
                  <div className={styles.highlightImageWrapper}>
                    <img
                      src={item.image}
                      alt={item.title}
                      className={styles.highlightImage}
                    />
                    <div className={styles.highlightOverlay} />
                    {item.badge && (
                      <span className={styles.highlightBadge}>{item.badge}</span>
                    )}
                  </div>
                  <p
                    style={{ ...typography.body.large.regular, color: colors.dark }}
                    className={styles.highlightTitle}
                  >
                    {item.title}
                  </p>
                  <span className={styles.highlightArrow}>→</span>
                </>
              );
              return item.external ? (
                <a
                  key={item.key}
                  href={item.link}
                  className={styles.highlightCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cardContent}
                </a>
              ) : (
                <Link key={item.key} to={toRouterPath(item.link)} className={styles.highlightCard}>
                  {cardContent}
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default HomePage;
